/**
 * Customer.io Web SDK Service
 *
 * Provides functions for initializing Customer.io and tracking events/users.
 * Mirrors the Mixpanel service pattern for consistency.
 */
import { useEffect, useRef } from 'react';
import { isProduction } from '@client/components/auth';
import * as enums from '@playbook-technologies/common-enums';
import {
  CustomerIOIdentifyPayload,
  CustomerIOTrackPayload,
} from './customerio.types';

let _isInitialized = false;
let _isInitializing = false;
let _initializationFailed = false;

const CUSTOMERIO_SCRIPT_ID = 'cio-tracker';

/**
 * Initialize Customer.io Web SDK
 * Loads the Customer.io script and sets up stub functions for queuing
 */
export const initializeCustomerIO = (siteId: string): void => {
  if (_isInitializing && !document.getElementById(CUSTOMERIO_SCRIPT_ID)) {
    _isInitializing = false;
  }
  if (_isInitialized || _isInitializing || _initializationFailed) return;
  if (!siteId) {
    console.warn('Customer.io: No Site ID provided, skipping initialization');
    return;
  }

  try {
    // Initialize _cio array if not exists (for queuing before script loads)
    window._cio = window._cio || ([] as unknown as typeof window._cio);

    // Define stub functions that queue calls before script loads
    const stubFunctions = [
      'identify',
      'track',
      'page',
      'reset',
      'on',
      'off',
    ] as const;
    stubFunctions.forEach((fn) => {
      if (!window._cio[fn]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window._cio as any)[fn] = (...args: unknown[]) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          window._cio.push([fn, ...args] as any);
        };
      }
    });

    const existingScript = document.getElementById(
      CUSTOMERIO_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existingScript) {
      const existingSiteId = existingScript.getAttribute('data-site-id');
      if (existingSiteId && existingSiteId !== siteId) {
        console.warn(
          `Customer.io: Script already present with different Site ID (${existingSiteId}), skipping initialization`,
        );
      }
      return;
    }

    // Create and inject Customer.io script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = CUSTOMERIO_SCRIPT_ID;
    script.setAttribute('data-site-id', siteId);
    script.setAttribute('data-use-array-params', 'true');
    script.setAttribute('data-use-in-app', 'true');
    script.src = 'https://assets.customer.io/assets/track.js';

    script.onload = () => {
      _isInitialized = true;
      _isInitializing = false;
      console.info('Customer.io initialized');
    };

    script.onerror = () => {
      _initializationFailed = true;
      _isInitializing = false;
      console.error('Customer.io failed to load');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    _isInitializing = true;
    firstScript.parentNode?.insertBefore(script, firstScript);
  } catch (error) {
    _initializationFailed = true;
    _isInitializing = false;
    console.error('Customer.io initialization error:', error);
  }
};

/**
 * Get Customer.io instance with safety checks
 */
const getCustomerIO = (): typeof window._cio | null => {
  if (_initializationFailed) return null;
  if (!window._cio) {
    console.warn('Customer.io not initialized');
    return null;
  }
  return window._cio;
};

/**
 * Identify user in Customer.io
 * Should be called on login and when user info changes
 */
export const customerIOIdentify = (
  payload: CustomerIOIdentifyPayload,
): void => {
  if (!isProduction()) {
    console.log('[Customer.io identify]:', payload);
  }

  const customerIO = getCustomerIO();
  if (!customerIO) return;

  try {
    customerIO.identify(payload);
  } catch (error) {
    console.error('Customer.io identify error:', error);
  }
};

/**
 * Track event in Customer.io
 * Event names match Mixpanel for consistency
 */
export const customerIOTrack = (
  eventName: string,
  properties?: CustomerIOTrackPayload,
): void => {
  if (!isProduction()) {
    console.log('[Customer.io track]:', eventName, properties);
  }

  const customerIO = getCustomerIO();
  if (!customerIO) return;

  try {
    customerIO.track(eventName, properties);
  } catch (error) {
    console.error('Customer.io track error:', error);
  }
};

/**
 * Track page view in Customer.io
 */
export const customerIOPage = (
  pageName?: string,
  properties?: CustomerIOTrackPayload,
): void => {
  if (!isProduction()) {
    console.log('[Customer.io page]:', pageName, properties);
  }

  const customerIO = getCustomerIO();
  if (!customerIO) return;

  try {
    customerIO.page(pageName, properties);
  } catch (error) {
    console.error('Customer.io page error:', error);
  }
};

/**
 * Reset/clear user identity (call on logout)
 */
export const customerIOReset = (): void => {
  if (!isProduction()) {
    console.log('[Customer.io reset]');
  }

  const customerIO = getCustomerIO();
  if (!customerIO) return;

  try {
    customerIO.reset();
  } catch (error) {
    console.error('Customer.io reset error:', error);
  }
};

/**
 * Check if Customer.io is ready
 */
export const isCustomerIOReady = (): boolean => {
  return _isInitialized && !_initializationFailed && !!window._cio;
};

type CustomerIOIdentifyParams = {
  influencerId: number | null | undefined;
  influencerEmail: string | null | undefined;
  influencerFirstName: string | null | undefined;
  influencerLastName: string | null | undefined;
  userRole: number | null | undefined;
};

/**
 * Hook to identify creator in Customer.io when GraphQL data is available.
 * Only identifies users with Influencer role (not admins viewing creator accounts).
 * Sends email along with other creator data for Customer.io messaging.
 */
export const useCustomerIOIdentify = ({
  influencerId,
  influencerEmail,
  influencerFirstName,
  influencerLastName,
  userRole,
}: CustomerIOIdentifyParams): void => {
  const identifiedEmail = useRef<string | null>(null);

  useEffect(() => {
    // Only identify creators with Influencer role
    if (userRole !== enums.PortalUser.Role.Influencer.value) return;
    if (!influencerId) return;
    if (!influencerEmail) return;
    if (identifiedEmail.current === influencerEmail) return;

    identifiedEmail.current = influencerEmail;

    customerIOIdentify({
      id: influencerEmail,
      influencerId,
      email: influencerEmail,
      first_name: influencerFirstName ?? undefined,
      last_name: influencerLastName ?? undefined,
    });
  }, [
    influencerId,
    influencerEmail,
    influencerFirstName,
    influencerLastName,
    userRole,
  ]);
};
